import { useRef, useState } from "react";
import { Form, required, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { SSOAuthButton } from "./SSOAuthButton";
import { enterApp } from "./enterApp";
import { useEnterAppWithSession } from "./useEnterAppWithSession";
import {
  disableEmailPasswordAuthentication,
  googleWorkplaceDomain,
} from "./authConfig";

export const LoginPage = (props: { redirectTo?: string }) => {
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const notify = useNotify();
  const translate = useTranslate();
  useEnterAppWithSession(props.redirectTo);

  const handleRequestCode: SubmitHandler<FieldValues> = async (values) => {
    setLoading(true);
    try {
      // OTP code by mail (no magic link or redirect).
      const { error } = await getSupabaseClient().auth.signInWithOtp({
        email: values.email,
      });
      if (error) throw error;
      setEmail(values.email);
      setCodeSent(true);
    } catch (error: any) {
      notify(error?.message || "ra.auth.sign_in_error", {
        type: "error",
        messageArgs: { _: error?.message },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (token = code) => {
    if (token.length < OTP_LEN || loading) return;
    setLoading(true);
    try {
      const { error } = await getSupabaseClient().auth.verifyOtp({
        email,
        token,
        type: "email",
      });
      if (error) throw error;
      // Session set: reload into the authenticated app.
      enterApp(props.redirectTo);
    } catch (error: any) {
      setCode("");
      notify(error?.message || "ra.auth.sign_in_error", {
        type: "error",
        messageArgs: { _: error?.message },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="relative grid w-full lg:grid-cols-2">
        <div className="relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
          <div className="absolute inset-0 bg-[#502379]" />
          <div className="relative z-20 flex items-center gap-3 text-lg font-medium">
            <img
              className="h-20"
              src="https://volt-fraktionen-hamburg.org/wp-content/uploads/2025/08/Website-Logo-Alle-Fraktionen-keine-Sterne-white.png"
              alt="Volt Fraktionen Hamburg"
            />
          </div>
        </div>
        <div className="flex flex-col justify-center w-full p-4 lg:p-8">
          <div className="w-full space-y-6 lg:mx-auto lg:w-[350px]">
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight">
                Anmelden
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Volt Hamburg Hub
              </p>
            </div>
            {disableEmailPasswordAuthentication ? null : codeSent ? (
              <form
                className="space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleVerifyCode();
                }}
              >
                <div className="flex flex-col items-center gap-3">
                  <Mail className="h-12 w-12 text-muted-foreground" />
                  <p className="text-center text-sm text-muted-foreground">
                    {translate("crm.auth.otp_sent", {
                      _: "We sent a login code to",
                    })}{" "}
                    <strong>{email}</strong>.{" "}
                    {translate("crm.auth.otp_check", {
                      _: "Enter the code from your inbox below.",
                    })}
                  </p>
                </div>
                <OtpInput
                  value={code}
                  onChange={setCode}
                  onComplete={handleVerifyCode}
                  disabled={loading}
                />
                <div className="flex flex-col gap-4">
                  <Button
                    type="submit"
                    className="cursor-pointer"
                    disabled={loading || code.length < OTP_LEN}
                  >
                    {translate("crm.auth.otp_verify", { _: "Sign In" })}
                  </Button>
                  <button
                    type="button"
                    className="text-sm hover:underline cursor-pointer"
                    onClick={() => {
                      setCodeSent(false);
                      setEmail("");
                      setCode("");
                    }}
                  >
                    {translate("crm.auth.otp_retry", {
                      _: "Use a different email",
                    })}
                  </button>
                </div>
              </form>
            ) : (
              <Form className="space-y-8" onSubmit={handleRequestCode}>
                <TextInput
                  label="ra.auth.email"
                  source="email"
                  type="email"
                  validate={required()}
                />
                <div className="flex flex-col gap-4">
                  <Button
                    type="submit"
                    className="cursor-pointer"
                    disabled={loading}
                  >
                    {translate("crm.auth.otp_send", {
                      _: "Send Login Code",
                    })}
                  </Button>
                </div>
              </Form>
            )}
            {googleWorkplaceDomain ? (
              <SSOAuthButton className="w-full" domain={googleWorkplaceDomain}>
                {translate("crm.auth.sign_in_google_workspace", {
                  _: "Sign in with Google Workplace",
                })}
              </SSOAuthButton>
            ) : null}
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};

const OTP_LEN = 8;

/** Segmentierter 8-stelliger Code-Block: Auto-Sprung, Backspace, Pfeiltasten, Paste, Auto-Submit. */
function OtpInput({
  value,
  onChange,
  onComplete,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  disabled?: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const commit = (next: string, focusIdx: number) => {
    const v = next.replace(/\D/g, "").slice(0, OTP_LEN);
    onChange(v);
    refs.current[Math.min(focusIdx, OTP_LEN - 1)]?.focus();
    if (v.length === OTP_LEN) onComplete?.(v);
  };

  const handleChange = (i: number, raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) return;
    const arr = value.split("");
    let pos = i;
    for (const ch of digits) {
      if (pos >= OTP_LEN) break;
      arr[pos] = ch;
      pos++;
    }
    commit(arr.join(""), pos);
  };

  const handleKeyDown = (
    i: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = value.split("");
      if (arr[i]) {
        arr[i] = "";
        commit(arr.join(""), i);
      } else if (i > 0) {
        arr[i - 1] = "";
        commit(arr.join(""), i - 1);
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < OTP_LEN - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  return (
    <div
      className="flex justify-center gap-1.5"
      onPaste={(e) => {
        e.preventDefault();
        const d = e.clipboardData
          .getData("text")
          .replace(/\D/g, "")
          .slice(0, OTP_LEN);
        if (d) commit(d, d.length);
      }}
    >
      {Array.from({ length: OTP_LEN }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          disabled={disabled}
          autoFocus={i === 0}
          value={value[i] ?? ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          aria-label={`Ziffer ${i + 1}`}
          className={`h-12 w-8 rounded-md border text-center text-lg font-semibold tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${
            value[i]
              ? "border-primary bg-primary/5"
              : "border-input bg-background"
          }`}
        />
      ))}
    </div>
  );
}
